import {
  PLANNING_UNDO_CHANGE_FIELDS,
  PLANNING_UNDO_CHANGE_TASK,
  PLANNING_UNDO_CHANGE_TASKS,
  PLANNING_UNDO_FIELD_LABELS,
  PLANNING_UNDO_PLAN_LABEL,
  PLANNING_UNDO_TASK_ADDED,
  PLANNING_UNDO_TASK_DELETED,
  PLANNING_UNDO_TASK_TYPE_LABELS,
  type PlanningUndoField,
} from 'constants/uiConstants';
import type { IInput } from 'interfaces/IInput';
import { InputTaskType, type IInputTask } from 'interfaces/IInputTask';

// Planning undo (OP-2539): the plan draft's earlier states, and what a step
// back or forward changes. Kept free of React so it can be unit-tested.

/** The plan-level fields undo puts back; the rest is the server's metadata. */
const HEADER_FIELDS = [
  'crop',
  'startDate',
  'endDate',
  'docName',
  'notes',
  'zone',
] as const;

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Who-changed-it fields: never put back, so a reverted task still reads as
// changed here and now.
const AUDIT_KEYS = new Set(['dateModified', 'modifierId', 'snapModifier']);

type TaskFields = Record<string, unknown>;

/**
 * Takes back one step — only what changed between `before` and `after` —
 * from the current draft, leaving everything else as it is now: plan fields
 * the step didn't touch, other tasks, and other fields of the tasks it did.
 * A task the step added is removed, one it deleted comes back whole (with
 * its products), and an edited task has just the edited fields put back.
 * Redo is the same with `before` and `after` swapped. Version and the other
 * metadata stay as they are now, so the autosave sends it as a normal edit.
 */
export const revertStep = (
  current: IInput,
  before: IInput,
  after: IInput
): IInput => {
  const reverted: IInput = { ...current };
  HEADER_FIELDS.forEach((field) => {
    if (!same(before[field], after[field])) {
      Object.assign(reverted, { [field]: before[field] });
    }
  });

  const beforeById = new Map(
    (before.tasks ?? []).map((task) => [task.taskId, task])
  );
  const afterById = new Map(
    (after.tasks ?? []).map((task) => [task.taskId, task])
  );
  let tasks = [...(current.tasks ?? [])];
  afterById.forEach((afterTask, taskId) => {
    const beforeTask = beforeById.get(taskId);
    if (!beforeTask) {
      tasks = tasks.filter((task) => task.taskId !== taskId);
      return;
    }
    if (same(beforeTask, afterTask)) return;
    tasks = tasks.map((task) => {
      if (task.taskId !== taskId) return task;
      const patch: Record<string, unknown> = {};
      const beforeRecord: TaskFields = Object.fromEntries(Object.entries(beforeTask));
      const afterRecord: TaskFields = Object.fromEntries(Object.entries(afterTask));
      const keys = new Set([
        ...Object.keys(beforeTask),
        ...Object.keys(afterTask),
      ]);
      keys.forEach((key) => {
        if (AUDIT_KEYS.has(key)) return;
        const beforeValue = beforeRecord[key];
        const afterValue = afterRecord[key];
        if (!same(beforeValue, afterValue)) patch[key] = beforeValue;
      });
      return { ...task, ...patch } as IInputTask;
    });
  });
  // Deleted by the step: back where it was, unless it's somehow here already.
  (before.tasks ?? []).forEach((beforeTask, index) => {
    if (afterById.has(beforeTask.taskId)) return;
    if (tasks.some((task) => task.taskId === beforeTask.taskId)) return;
    tasks.splice(Math.min(index, tasks.length), 0, beforeTask);
  });
  reverted.tasks = tasks;
  return reverted;
};

/** True when the two drafts' plan fields and tasks are the same. */
export const isSameDraftContent = (a: IInput, b: IInput): boolean =>
  HEADER_FIELDS.every((field) => same(a[field], b[field])) &&
  same(a.tasks ?? [], b.tasks ?? []);

/** The task saves a restore needs: tasks to put back or update, and tasks to delete. */
export const diffDraftTasks = (
  current: IInputTask[],
  target: IInputTask[]
): { upserts: IInputTask[]; deletes: string[] } => {
  const currentById = new Map(current.map((task) => [task.taskId, task]));
  const targetIds = new Set(target.map((task) => task.taskId));
  return {
    upserts: target.filter((task) => !same(currentById.get(task.taskId), task)),
    deletes: current
      .filter((task) => !targetIds.has(task.taskId))
      .map((task) => task.taskId),
  };
};

/** "Glyphosate", "Glyphosate +2" or the task's type, e.g. "crop establishment". */
export const describeTask = (task: IInputTask): string => {
  const names = (task.steps ?? [])
    .map((step) => step.item?.trim())
    .filter((name): name is string => !!name);
  if (names.length === 0) {
    return (
      PLANNING_UNDO_TASK_TYPE_LABELS[task.taskType] ??
      PLANNING_UNDO_TASK_TYPE_LABELS[InputTaskType.Generic]
    );
  }
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
};

const changedFields = (from: IInput, to: IInput): PlanningUndoField[] => {
  const fields: PlanningUndoField[] = [];
  if (
    from.crop?.cropName !== to.crop?.cropName ||
    from.crop?.globalId !== to.crop?.globalId
  ) {
    fields.push('crop');
  }
  if (!same(from.crop?.variety, to.crop?.variety)) fields.push('variety');
  if (!same(from.startDate, to.startDate)) fields.push('startDate');
  if (!same(from.endDate, to.endDate)) fields.push('endDate');
  if (!same(from.zone?.zoneArea, to.zone?.zoneArea)) fields.push('area');
  if (!same(from.zone?.areaUnit, to.zone?.areaUnit)) fields.push('areaUnit');
  if (!same(from.docName, to.docName) || !same(from.notes, to.notes)) {
    fields.push('details');
  }
  return fields;
};

/**
 * The change that turned `before` into `after`, in the adviser's words —
 * e.g. "adding Glyphosate", "deleting Crop establishment", "changes to
 * Urea" or "change to start date". The note after an undo or redo names
 * it (Undone: … / Redone: …). Task changes come first: a sowing date that
 * also moved the start date reads as the sowing change.
 */
export const describeDraftChange = (after: IInput, before: IInput): string => {
  const beforeTasks = before.tasks ?? [];
  const afterTasks = after.tasks ?? [];
  const beforeById = new Map(beforeTasks.map((task) => [task.taskId, task]));
  const afterIds = new Set(afterTasks.map((task) => task.taskId));
  const added = afterTasks.filter((task) => !beforeById.has(task.taskId));
  const deleted = beforeTasks.filter((task) => !afterIds.has(task.taskId));
  const edited = afterTasks.filter((task) => {
    const previous = beforeById.get(task.taskId);
    return previous && !same(previous, task);
  });

  const taskCount = added.length + deleted.length + edited.length;
  if (taskCount > 1) return PLANNING_UNDO_CHANGE_TASKS(taskCount);
  if (added.length === 1) {
    return PLANNING_UNDO_TASK_ADDED(describeTask(added[0]));
  }
  if (deleted.length === 1) {
    return PLANNING_UNDO_TASK_DELETED(describeTask(deleted[0]));
  }
  if (edited.length === 1) {
    return PLANNING_UNDO_CHANGE_TASK(describeTask(edited[0]));
  }

  const fields = changedFields(before, after);
  if (fields.length === 0) return PLANNING_UNDO_PLAN_LABEL;
  return PLANNING_UNDO_CHANGE_FIELDS(
    fields.map((field) => PLANNING_UNDO_FIELD_LABELS[field])
  );
};

/** One step of undo history: the draft before and after a change. */
export interface DraftHistoryEntry {
  before: IInput;
  /**
   * The draft once the step was done — set when the next step starts; the
   * newest step's is the draft as it is now.
   */
  after: IInput | null;
  /** What changed — consecutive changes to the same thing are one step. */
  key: string;
  /** When the step was last extended, in ms. */
  at: number;
}

/**
 * The undo stack after a change. A change to the same thing as the last
 * step within the group window (typing in a cell, a field committed twice)
 * extends that step instead of adding one, so it's undone in one go.
 * Otherwise the last step is closed with `before` as its after state.
 */
export const pushHistoryEntry = (
  stack: DraftHistoryEntry[],
  before: IInput,
  key: string,
  now: number,
  groupWindowMs: number,
  limit: number
): DraftHistoryEntry[] => {
  const top = stack[stack.length - 1];
  if (top && !top.after && top.key === key && now - top.at <= groupWindowMs) {
    return [...stack.slice(0, -1), { ...top, at: now }];
  }
  const closed =
    top && !top.after
      ? [...stack.slice(0, -1), { ...top, after: before }]
      : stack;
  return [...closed, { before, after: null, key, at: now }].slice(-limit);
};
